import {useMemo} from 'react';

import inferTypeFromLookupConfig from 'lookup-config/inferTypeFromLookupConfig';
import {LookupConfigDetailPaneProps} from 'lookup-config/LookupConfigDetailPane';

import {LookupStepSpec, LookupStepType} from '../LookupConfigModalConstants';

/** These LookupStepTypes do not sort their contents on the backend */
const PRESERVE_ORDER_LOOKUP_TYPES = [LookupStepType.BOX_CONTAINERS, LookupStepType.WELLS];

/** Determines whether the Lookup Config Modal should show the option to set the sorting option of Lookups.
 *
 * We want to give users the option to sort their results alphabetically or retain original order if their
 * lookup returns multiple results.
 * This is only relevant for Notebook Lookup Columns as we don't generally allow customizing sorting for other
 * lookup usages.
 */
export function useShowFinalSortOption(
  props: Pick<
    LookupConfigDetailPaneProps,
    'detailStep' | 'isMulti' | 'multiFlattenLevel' | 'previousLookupStepInfos'
  > & {isNotebookLookup: boolean}
): boolean {
  const {detailStep, isMulti, multiFlattenLevel, previousLookupStepInfos, isNotebookLookup} = props;
  return useMemo(() => {
    if (!isNotebookLookup) {
      return false;
    }
    // Some LookupStepTypes do not sort their values on the backend, so don't show the option here
    if (detailStep && PRESERVE_ORDER_LOOKUP_TYPES.includes(detailStep.lookupStep.type)) {
      return false;
    }
    const isMultiplex = isLookupMultiplex({detailStep, isMulti, multiFlattenLevel, previousLookupStepInfos});
    return isMultiplex ?? false;
  }, [detailStep, isMulti, multiFlattenLevel, previousLookupStepInfos, isNotebookLookup]);
}

/** Returns whether the current Lookup Config is multiplex.
 *
 * If it cannot be determined for some reason, returns undefined.
 */
function isLookupMultiplex(
  props: Pick<
    LookupConfigDetailPaneProps,
    'detailStep' | 'isMulti' | 'multiFlattenLevel' | 'previousLookupStepInfos'
  >
): boolean | undefined {
  const {previousLookupStepInfos, detailStep, isMulti, multiFlattenLevel} = props;
  const startingSchema = previousLookupStepInfos.find(
    (lookupStepInfo) => lookupStepInfo.linkSchema != null
  )?.linkSchema;
  if (!startingSchema) {
    return undefined;
  }
  // collect the current selected lookup steps
  const lookupSteps: Array<LookupStepSpec> = [
    ...previousLookupStepInfos,
    ...(detailStep ? [detailStep] : []),
  ].reduce<Array<LookupStepSpec>>((collectedStepSpecs, lookupStepInfo) => {
    const lookupStepSpecs: Array<LookupStepSpec> = [];
    if (lookupStepInfo.lookupStep) {
      lookupStepSpecs.push(lookupStepInfo.lookupStep);
    }
    // add any collapsing lookup steps as they can affect multiplexing
    if (lookupStepInfo.collapsingLookupSteps) {
      lookupStepSpecs.push(...lookupStepInfo.collapsingLookupSteps.map((step) => step.lookupStep));
    }
    return collectedStepSpecs.concat(lookupStepSpecs);
  }, []);
  // determine if the final results of the lookup has multiple values
  const {isMultiplex} = inferTypeFromLookupConfig({isMulti, multiFlattenLevel, lookupSteps}, startingSchema);
  return isMultiplex;
}
